import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AskAnswer, AskCard, PageRef } from '@shared/api';
import { Markdown } from '../../lib/markdown';
import { toolPhrase, type TaskRow } from '../../state/taskRows';
import { ThinkingAuraText } from './ThinkingAuraText';
import type { ThinkingAuraSettings } from './thinkingAura';
import type { StatusQueueEntry } from './statusQueue';
import { useStatusQueue } from './useStatusQueue';

/**
 * The task list block (docs/message-bar-states.md, docs/composer-cards.md
 * §2). One row per state, rendered in the order `taskRows()` already
 * produces: the last turn (running / waiting / done), then anything queued
 * behind it.
 */
export function TaskList({
  rows,
  onSend,
  onAskAnswer,
  onStop,
  onRemoveQueued,
  onDismiss,
  onUndo,
  auraSettings,
  reducedMotion,
}: {
  rows: TaskRow[];
  /** Sends a piece of text as the rep's next message — a tapped answer, or a Retry of a past request. */
  onSend: (text: string) => void;
  /** Routes ask answers with their source row so workflow-owned asks can perform a direct action. */
  onAskAnswer: (row: TaskRow, text: string) => void;
  onStop: () => void;
  onRemoveQueued: (userId: string) => void;
  onDismiss: () => void;
  /** The done row's Undo — carries the row so the caller can restore its versionAtSend (docs/composer-cards.md 5c). */
  onUndo: (row: TaskRow) => void;
  auraSettings: ThinkingAuraSettings;
  reducedMotion: boolean;
}): ReactElement {
  return (
    <div className="dock-tasks">
      {rows.map((row) => (
        <TaskRowItem
          key={row.key}
          row={row}
          onSend={onSend}
          onAskAnswer={(text) => onAskAnswer(row, text)}
          onStop={onStop}
          onRemove={() => onRemoveQueued(row.key)}
          onDismiss={onDismiss}
          onUndo={() => onUndo(row)}
          auraSettings={auraSettings}
          reducedMotion={reducedMotion}
        />
      ))}
    </div>
  );
}

/**
 * The page chip (Changes §2): "04 Strategy" in the mock's tile-caption mono
 * style, the number in accent. `page: null` renders nothing — "no page, no
 * chip" — so every call site can pass `row.page` unconditionally.
 */
function PageChip({ page }: { page: PageRef | null }): ReactElement | null {
  if (!page) return null;
  return (
    <span className="dock-row-page-chip">
      <span className="dock-row-page-number">{String(page.number).padStart(2, '0')}</span> {page.title}
    </span>
  );
}

function StopIcon(): ReactElement {
  return (
    <svg className="dock-row-stop-icon" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <circle cx="9" cy="9" r="7.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <rect x="6.25" y="6.25" width="5.5" height="5.5" rx="0.75" fill="currentColor" />
    </svg>
  );
}

/** Change and Stopped rows close themselves this long after they land (docs/message-bar-states.md §5). */
const AUTO_CLOSE_MS = 6000;
const ROW_CONTENT_TRANSITION_MS = 260;
const LAYOUT_TRANSITION_FALLBACK_MS = 600;

function ThinkingStatusText({ label, settings, reducedMotion }: {
  label: string;
  settings: ThinkingAuraSettings;
  reducedMotion: boolean;
}): ReactElement {
  return (
    <ThinkingAuraText
      className="dock-row-status"
      aria-live="polite"
      settings={settings}
      reducedMotion={reducedMotion}
    >
      {label}
    </ThinkingAuraText>
  );
}

interface LayoutSnapshot {
  key: string;
  className: string;
  content: ReactNode;
}

function TaskLayoutTransition({ layoutKey, layoutClassName, reducedMotion, children }: {
  layoutKey: string;
  layoutClassName: string;
  reducedMotion: boolean;
  children: ReactNode;
}): ReactElement {
  const latestRef = useRef<LayoutSnapshot>({ key: layoutKey, className: layoutClassName, content: children });
  const [renderedKey, setRenderedKey] = useState(layoutKey);
  const [outgoing, setOutgoing] = useState<LayoutSnapshot | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  const incomingRef = useRef<HTMLDivElement>(null);

  // Capture the previous layout after React commits but before the browser
  // paints. Keeping ref writes out of render makes the snapshot reliable in
  // Strict Mode while the synchronous layout-effect update prevents a frame
  // where the incoming layout appears by itself.
  useLayoutEffect(() => {
    const next = { key: layoutKey, className: layoutClassName, content: children };
    if (layoutKey !== renderedKey) {
      const previous = latestRef.current;
      latestRef.current = next;
      setOutgoing(previous);
      setRenderedKey(layoutKey);
      return;
    }
    latestRef.current = next;
  }, [children, layoutClassName, layoutKey, renderedKey]);

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!outgoing || !host) return undefined;
    // aria-hidden removes the duplicate layout from the accessibility tree;
    // inert also removes any buttons inside it from sequential focus.
    outgoingRef.current?.setAttribute('inert', '');
    if (reducedMotion) {
      setOutgoing(null);
      host.style.height = '';
      host.style.overflow = '';
      host.style.transition = '';
      delete host.dataset.layoutDirection;
      return undefined;
    }

    // Rebase interrupted transitions from the height that is actually on
    // screen. Measure each layer's layout box so translated entrance content
    // cannot inflate the destination with visual overflow.
    const currentHeight = host.getBoundingClientRect().height;
    const fromHeight = host.style.height
      ? currentHeight
      : outgoingRef.current?.getBoundingClientRect().height ?? currentHeight;
    const toHeight = incomingRef.current?.getBoundingClientRect().height ?? fromHeight;
    const delta = toHeight - fromHeight;
    host.dataset.layoutDirection = Math.abs(delta) < 1 ? 'steady' : delta > 0 ? 'expand' : 'collapse';
    host.style.transition = 'none';
    host.style.height = `${fromHeight}px`;
    host.style.overflow = 'hidden';
    // Commit the current visual height before restoring the direction-aware
    // CSS transition and moving to the new layout on the next frame.
    host.getBoundingClientRect();
    const frame = window.requestAnimationFrame(() => {
      host.style.transition = '';
      host.style.height = `${toHeight}px`;
    });
    let settled = false;
    let timeout = 0;
    const settle = () => {
      if (settled) return;
      settled = true;
      host.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(timeout);
      setOutgoing(null);
      host.style.height = '';
      host.style.overflow = '';
      host.style.transition = '';
      delete host.dataset.layoutDirection;
    };
    const onTransitionEnd = (event: TransitionEvent) => {
      if (event.target === host && event.propertyName === 'height') settle();
    };
    host.addEventListener('transitionend', onTransitionEnd);
    // If the measured heights are effectively equal the browser may not emit
    // transitionend, so retain a safety net beyond the longest CSS duration.
    timeout = window.setTimeout(settle, LAYOUT_TRANSITION_FALLBACK_MS);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      host.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [outgoing, renderedKey, reducedMotion]);

  return (
    <div ref={hostRef} className={`dock-layout-transition${outgoing ? ' dock-layout-transition-active' : ''}`}>
      {outgoing ? (
        <div
          ref={outgoingRef}
          className={`dock-layout-layer dock-layout-layer-exit ${outgoing.className}`}
          aria-hidden="true"
        >
          {outgoing.content}
        </div>
      ) : null}
      <div
        ref={incomingRef}
        key={renderedKey}
        className={`dock-layout-layer ${outgoing ? 'dock-layout-layer-enter' : 'dock-layout-layer-stable'} ${layoutClassName}`}
      >
        {children}
      </div>
    </div>
  );
}

function taskLayoutKey(row: TaskRow): string {
  if (row.state === 'waiting') return `waiting-${row.ask ? askLayout(row.ask.answers) : 'empty'}-${row.ask?.answers.length ?? 0}${row.working ? '-working' : ''}`;
  if (row.state === 'done') return `done-${row.kind ?? 'answer'}`;
  return row.state;
}

/**
 * One row, one element, for the life of a task (Changes §3, animation-ready
 * structure). `row.key` (the task id: the user message id that started the
 * turn) never changes across running → waiting → done, and neither does the
 * DOM node this component owns — every state below only ever changes this
 * div's className and children, never the component itself. That matters
 * because React remounts on a type change even when the key stays the same:
 * rendering a *different* per-state component (a `RunningRow`, a `DoneRow`,
 * ...) at the same key would still tear down and rebuild the DOM on every
 * transition. Branch inside this one component instead, so a later
 * CSS-only pass can crossfade or morph between states without fighting a
 * remount.
 */
function TaskRowItem({
  row,
  onSend,
  onAskAnswer,
  onStop,
  onRemove,
  onDismiss,
  onUndo,
  auraSettings,
  reducedMotion,
}: {
  row: TaskRow;
  onSend: (text: string) => void;
  onAskAnswer: (text: string) => void;
  onStop: () => void;
  onRemove: () => void;
  onDismiss: () => void;
  onUndo: () => void;
  auraSettings: ThinkingAuraSettings;
  reducedMotion: boolean;
}): ReactElement | null {
  // Done-row auto-close bookkeeping lives here — not in a per-state
  // component — so the same hook instance (and timers) carries a task
  // across every state it passes through; see the component comment above.
  // A change or stopped row closes itself after AUTO_CLOSE_MS unless the
  // rep is hovering or has focus inside it, in which case the remaining
  // time is banked and the countdown picks back up on mouse-leave/blur.
  // A change row that also carries the agent's reply ("send me their URL")
  // stays open like an answer — the reply may be the only place the rep
  // learns what the agent is waiting on (proposal 2b6d74f3).
  const autoCloses =
    row.state === 'done' && (row.kind === 'stopped' || (row.kind === 'change' && !row.text.trim()));
  const timerRef = useRef<number | null>(null);
  const timerStartDelayRef = useRef<number | null>(null);
  const progressFrameRef = useRef<number | null>(null);
  const remainingRef = useRef(AUTO_CLOSE_MS);
  const startedAtRef = useRef(0);
  const [remainingMs, setRemainingMs] = useState(AUTO_CLOSE_MS);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const clearTimerStartDelay = () => {
    if (timerStartDelayRef.current !== null) {
      window.clearTimeout(timerStartDelayRef.current);
      timerStartDelayRef.current = null;
    }
  };
  const stopProgress = () => {
    if (progressFrameRef.current !== null) {
      window.cancelAnimationFrame(progressFrameRef.current);
      progressFrameRef.current = null;
    }
  };
  const updateProgress = () => {
    const remaining = Math.max(0, remainingRef.current - (Date.now() - startedAtRef.current));
    setRemainingMs(remaining);
    return remaining;
  };
  const runProgress = () => {
    stopProgress();
    const tick = () => {
      if (updateProgress() <= 0) {
        progressFrameRef.current = null;
        return;
      }
      progressFrameRef.current = window.requestAnimationFrame(tick);
    };
    progressFrameRef.current = window.requestAnimationFrame(tick);
  };
  const schedule = (ms: number) => {
    startedAtRef.current = Date.now();
    remainingRef.current = ms;
    setRemainingMs(ms);
    timerRef.current = window.setTimeout(onDismiss, ms);
    runProgress();
  };

  useEffect(() => {
    if (!autoCloses) return undefined;
    remainingRef.current = AUTO_CLOSE_MS;
    setRemainingMs(AUTO_CLOSE_MS);
    if (reducedMotion) {
      schedule(AUTO_CLOSE_MS);
    } else {
      timerStartDelayRef.current = window.setTimeout(() => {
        timerStartDelayRef.current = null;
        schedule(AUTO_CLOSE_MS);
      }, ROW_CONTENT_TRANSITION_MS);
    }
    return () => {
      clearTimerStartDelay();
      clearTimer();
      stopProgress();
    };
    // Re-arms only when the row itself changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.key, autoCloses, reducedMotion]);

  const pause = () => {
    if (!autoCloses) return;
    if (timerStartDelayRef.current !== null) {
      clearTimerStartDelay();
      return;
    }
    if (timerRef.current === null) return;
    remainingRef.current = updateProgress();
    clearTimer();
    stopProgress();
  };
  const resume = () => {
    if (!autoCloses || timerRef.current !== null) return;
    schedule(remainingRef.current > 0 ? remainingRef.current : AUTO_CLOSE_MS);
  };

  // Fast tools finish in milliseconds; without a floor their status label
  // gets replaced before anyone can read it. status.label (once a `status`
  // event has arrived) is the label, toolPhrase(activeTool) only the
  // fallback for the window before the first one lands — useStatusQueue
  // holds each one on screen for STATUS_MIN_DISPLAY_MS before advancing to
  // whatever arrived meanwhile (only the newest survives a hold). Called
  // unconditionally (rules of hooks): null outside the running state, which
  // also drops anything the queue was holding back.
  // A waiting row whose turn is still running behind a keepWorking question
  // (row.working, docs/decisions.md §6) keeps the same live status line.
  const showsStatus = row.state === 'running' || (row.state === 'waiting' && row.working === true);
  const runningEntry: StatusQueueEntry | null =
    showsStatus ? { label: row.status?.label ?? toolPhrase(row.activeTool), page: row.page } : null;
  const displayedStatus = useStatusQueue(runningEntry);

  let className = 'dock-row';
  let content: ReactNode;
  let handlers: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
  } = {};

  switch (row.state) {
    case 'running': {
      className += ' dock-row-running';
      // The page chip follows whichever label is being shown, not the
      // newest page a status event named — both are held and advanced as
      // one unit by useStatusQueue.
      const label = displayedStatus?.label ?? runningEntry?.label ?? '';
      const chipPage = displayedStatus?.page ?? runningEntry?.page ?? null;
      content = (
        <>
          <span className="dock-row-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <ThinkingStatusText label={label} settings={auraSettings} reducedMotion={reducedMotion} />
          <PageChip page={chipPage} />
          <button type="button" className="dock-row-action" onClick={onStop} aria-label="Stop">
            <StopIcon />
          </button>
        </>
      );
      break;
    }
    case 'queued':
      className += ' dock-row-queued';
      content = (
        <>
          <span className="dock-row-queued-text">{row.request}</span>
          <PageChip page={row.page} />
          <button type="button" className="dock-row-remove" onClick={onRemove} aria-label="Remove from queue">
            ✕
          </button>
        </>
      );
      break;
    case 'waiting': {
      const ask = row.ask;
      if (!ask) return null;
      className += ' dock-row-waiting';
      // Still working behind the question: the running row's status line
      // (dots, label, page chip, Stop) sits above the question so the rep
      // sees "Researching…" and the answers at once.
      const status = row.working ? (
        <div className="dock-row-waiting-status">
          <span className="dock-row-dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <ThinkingStatusText
            label={displayedStatus?.label ?? runningEntry?.label ?? ''}
            settings={auraSettings}
            reducedMotion={reducedMotion}
          />
          <PageChip page={displayedStatus?.page ?? runningEntry?.page ?? null} />
          <button type="button" className="dock-row-action" onClick={onStop} aria-label="Stop">
            <StopIcon />
          </button>
        </div>
      ) : null;
      content = <WaitingContent row={row} ask={ask} status={status} onSend={onAskAnswer} onSkip={onDismiss} />;
      break;
    }
    case 'done':
      className += ` dock-row-done dock-row-done-${row.kind ?? 'answer'}`;
      handlers = { onMouseEnter: pause, onMouseLeave: resume, onFocus: pause, onBlur: resume };
      content = (
        <DoneContent
          row={row}
          onRetry={() => onSend(row.request)}
          onUndo={onUndo}
          onClose={onDismiss}
          autoCloses={autoCloses}
          remainingMs={remainingMs}
        />
      );
      break;
    default:
      return null;
  }

  return (
    <div className={className} {...handlers}>
      <TaskLayoutTransition
        layoutKey={taskLayoutKey(row)}
        layoutClassName={className.replace(/^dock-row\s*/, '')}
        reducedMotion={reducedMotion}
      >
        {content}
      </TaskLayoutTransition>
    </div>
  );
}

/** The layout is picked from the answers, never by the agent (docs/composer-cards.md 4b). */
function askLayout(answers: AskAnswer[]): 'gallery' | 'rows' | 'pills' {
  if (answers.some((answer) => answer.imageUrl)) return 'gallery';
  // One answer is an explicit confirmation action. Keep it as the primary
  // pill even when its label is long (for example, "Approve brief & build
  // draft") instead of demoting it to a neutral option row.
  if (answers.length === 1) return 'pills';
  if (answers.length > 2 || answers.some((answer) => answer.label.length > 24)) return 'rows';
  return 'pills';
}

function WaitingContent({
  row,
  ask,
  status,
  onSend,
  onSkip,
}: {
  row: TaskRow;
  ask: AskCard;
  /** The running status line, present only while the turn is still working behind the question (row.working). */
  status?: ReactNode;
  onSend: (text: string) => void;
  onSkip: () => void;
}): ReactElement {
  const layout = askLayout(ask.answers);

  return (
    <>
      {status}
      {layout !== 'gallery' && row.text ? <p className="dock-row-lead">{row.text}</p> : null}
      <div className="dock-row-waiting-top">
        <h3 className="dock-row-question">{ask.question}</h3>
        <PageChip page={row.page} />
        {row.dismissible === false ? null : (
          <button type="button" className="dock-row-skip" onClick={onSkip}>
            Skip
          </button>
        )}
      </div>
      {layout === 'gallery' ? (
        <AskGallery answers={ask.answers} onSend={onSend} />
      ) : layout === 'rows' ? (
        <div className="dock-ask-rows">
          {ask.answers.map((answer, index) => (
            <button key={index} type="button" className="dock-ask-row" onClick={() => onSend(answer.label)}>
              {answer.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="dock-ask-pills">
          {ask.answers.map((answer, index) => (
            <button
              key={index}
              type="button"
              className={`dock-ask-pill${index === ask.recommended ? ' dock-ask-pill-accent' : ''}`}
              onClick={() => onSend(answer.label)}
            >
              {answer.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function AskGallery({
  answers,
  onSend,
}: {
  answers: AskAnswer[];
  onSend: (text: string) => void;
}): ReactElement {
  const [selected, setSelected] = useState<number | null>(null);
  const tiles = answers.slice(0, 4);

  return (
    <div className="dock-ask-gallery">
      <div className="dock-ask-gallery-grid" style={{ '--tiles': tiles.length } as CSSProperties}>
        {tiles.map((answer, index) => {
          const classes = ['dock-ask-tile'];
          if (answer.imageUrl) classes.push('dock-ask-tile-photo');
          if (selected === index) classes.push('dock-ask-tile-selected');
          return (
            <button
              key={index}
              type="button"
              className={classes.join(' ')}
              onClick={() => setSelected(index)}
              aria-pressed={selected === index}
            >
              {answer.imageUrl ? (
                <img src={answer.imageUrl} alt={answer.label} className="dock-ask-tile-image" />
              ) : (
                <span className="dock-ask-tile-placeholder" aria-hidden="true" />
              )}
              <span className="dock-ask-tile-caption">{answer.label}</span>
              {selected === index ? (
                <span className="dock-ask-tile-badge" aria-hidden="true">
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.4">
                    <path d="M2.4 6.4 4.8 8.8 9.6 3.6" />
                  </svg>
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="dock-ask-gallery-use"
        disabled={selected === null}
        onClick={() => {
          if (selected === null) return;
          const answer = tiles[selected];
          if (answer) onSend(answer.label);
        }}
      >
        Use this one
      </button>
    </div>
  );
}

function DoneContent({
  row,
  onRetry,
  onUndo,
  onClose,
  autoCloses,
  remainingMs,
}: {
  row: TaskRow;
  onRetry: () => void;
  onUndo: () => void;
  /** Ok's click, and the auto-close timer, both just close the row — the same thing Skip used to do. */
  onClose: () => void;
  autoCloses: boolean;
  remainingMs: number;
}): ReactElement {
  const showUndo = row.kind === 'change' && row.undo;
  const changeWithReply = row.kind === 'change' && row.text.trim().length > 0;

  return (
    <>
      <div className="dock-row-done-top">
        <PageChip page={row.page} />
        {row.kind === 'answer' ? (
          <button type="button" className="dock-row-top-action" onClick={onClose}>
            Ok
          </button>
        ) : row.kind === 'error' ? (
          <button type="button" className="dock-row-top-action" onClick={onRetry}>
            Retry
          </button>
        ) : null}
        {showUndo ? (
          <button type="button" className="dock-row-top-action" onClick={onUndo}>
            Undo
          </button>
        ) : null}
        {changeWithReply ? (
          <button type="button" className="dock-row-top-action" onClick={onClose}>
            Ok
          </button>
        ) : null}
        {autoCloses ? (
          <div className="dock-row-auto-close-progress" aria-hidden="true">
            <span style={{ transform: `scaleX(${Math.max(0, Math.min(1, remainingMs / AUTO_CLOSE_MS))})` }} />
          </div>
        ) : null}
      </div>
      <div className="dock-row-done-message">
        {changeWithReply ? (
          <>
            <p className="dock-row-done-line">{row.summaryLine}</p>
            <Markdown text={row.text} />
          </>
        ) : row.kind === 'change' || row.kind === 'stopped' ? (
          <p className="dock-row-done-line">{row.summaryLine}</p>
        ) : (
          <Markdown text={row.kind === 'error' ? row.error ?? '' : row.text} />
        )}
      </div>
    </>
  );
}
